import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Badge,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import RABillAside from "./raBillAside";
import { formatCurrency } from "../../utils/Utils";
import { mockRABills } from "./mockRABills";

const { Option } = Select;
const { RangePicker } = DatePicker;

const RABillsPendingApprovals: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [pendingRABills, setPendingRABills] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    stage: "",
    contractor: "",
    raBillNumber: "",
    woNumber: "",
    dateRange: null as any,
    slaStatus: "",
    site: "",
  });

  useEffect(() => {
    loadPendingRABills();
  }, []);

  const loadPendingRABills = () => {
    setLoading(true);
    try {
      // Filter RA Bills that are pending approval
      const pendingBills = mockRABills.filter((bill) =>
        ["Submitted", "Checked", "Verified"].includes(bill.status)
      );
      setPendingRABills(pendingBills);
    } catch (error) {
      console.error("Error loading pending RA Bills:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Submitted: "processing",
      Checked: "warning",
      Verified: "success",
      Approved: "success",
      Returned: "error",
    };
    return statusColors[status] || "default";
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case "Submitted":
        return "📝";
      case "Checked":
        return "✅";
      case "Verified":
        return "🔍";
      default:
        return "📋";
    }
  };

  const handleOpenRABill = (record: any) => {
    navigate(
      `${process.env.PUBLIC_URL}/ra-bills-view-details/${record.raBillId}?mode=approval`
    );
  };

  const resetFilters = () => {
    setFilters({
      stage: "",
      contractor: "",
      raBillNumber: "",
      woNumber: "",
      dateRange: null,
      slaStatus: "",
      site: "",
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  // KPI calculations
  const submittedCount = pendingRABills.filter(
    (bill) => bill.status === "Submitted"
  ).length;
  const checkedCount = pendingRABills.filter(
    (bill) => bill.status === "Checked"
  ).length;
  const verifiedCount = pendingRABills.filter(
    (bill) => bill.status === "Verified"
  ).length;
  const slaBreachCount = pendingRABills.filter(
    (bill) => bill.slaAge > 2
  ).length;

  const columns: TableColumnsType<any> = [
    {
      title: "RA Bill #",
      dataIndex: "raBillSequenceId",
      key: "raBillSequenceId",
      width: 180,
      fixed: "left",
      render: (text: string, record: any) => (
        <span
          className="inlineTableCellLink"
          id={`raBillStatus-${record?.raBillId}`}
          onClick={() => handleOpenRABill(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 180,
      // render: (text, record) => (
      //   <div>
      //     <div className="fw-bold text-primary">{text}</div>
      //     <div className="text-muted small mt-1">Section: {record.section}</div>
      //   </div>
      // ),
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 200,
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName}</div>
        </div>
      ),
    },
    {
      title: "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 180,
    },
    {
      title: "RA Period",
      key: "raPeriod",
      width: 140,
      render: (_, record) => (
        <div>
          <div className="fw-bold">
            {dayjs(record.raPeriodFrom).format("DD-MMM")}
          </div>
          <div className="text-muted small">
            to {dayjs(record.raPeriodTo).format("DD-MMM-YY")}
          </div>
        </div>
      ),
    },
    {
      title: "Stage",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (stage, record) => (
        <div>
          <Tag color={getStatusColor(record.status)}>
            {getStageIcon(stage)} {stage}
          </Tag>
        </div>
      ),
    },
    {
      title: "Submitted On/By",
      key: "submitted",
      width: 160,
      render: (_, record) => (
        <div>
          <div className="fw-bold">
            {dayjs(record.createdAt).format("DD-MMM-YYYY")}
          </div>
          <div className="text-muted small">{record.createdBy}</div>
        </div>
      ),
    },
    {
      title: "Last Action By",
      key: "lastAction",
      width: 160,
      render: (_, record) => (
        <div>
          {record.lastActionBy ? (
            <>
              <div className="fw-bold">{record.lastActionBy}</div>
              <div className="text-muted small">
                {dayjs(record.lastActionDate).format("DD-MMM HH:mm")}
              </div>
            </>
          ) : (
            <span className="text-muted">---</span>
          )}
        </div>
      ),
    },
    {
      title: "Current Bill Value",
      dataIndex: "grossTotal",
      key: "grossTotal",
      width: 150,
      align: "right",
      render: (value) => (
        <div className="fw-bold text-primary">
          {formatCurrency(value, "INR")}
        </div>
      ),
      sorter: (a, b) => a.grossTotal - b.grossTotal,
    },
    {
      title: "SLA Age",
      dataIndex: "slaAge",
      key: "slaAge",
      width: 100,
      align: "center",
      render: (days) => (
        <Badge
          count={`${days}d`}
          color={days > 2 ? "#ff4d4f" : days > 1 ? "#faad14" : "#52c41a"}
        />
      ),
      sorter: (a, b) => a.slaAge - b.slaAge,
    },
    // {
    //   title: "Flags",
    //   key: "flags",
    //   width: 100,
    //   render: (_, record) => (
    //     <div>
    //       {record.attachments && record.attachments.length > 0 && (
    //         <span title="Documents Attached">📎 </span>
    //       )}
    //       {record.slaAge > 2 && (
    //         <span title="SLA Breach">🔶 </span>
    //       )}
    //       {record.softHold && (
    //         <span title="Soft Hold">⚠️ </span>
    //       )}
    //     </div>
    //   ),
    // },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 150,
    //   render: (_, record) => (
    //     <Space wrap>
    //       <Button
    //         type="primary"
    //         size="small"
    //         onClick={() => handleOpenRABill(record)}
    //       >
    //         Review
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="ra-bills-pending-approvals"
      title="Pending RA Bill Approvals"
      subtitle="Review and approve submitted Running Account Bills"
      columns={columns}
      dataSource={pendingRABills}
      loading={loading}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <RABillAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1800,
        showSearch: true,
        searchPlaceholder: "Search by RA Bill#, WO#, or Contractor...",
        kpiWidgets: [
          {
            title: "Submitted",
            value: submittedCount,
            icon: <ClockCircleOutlined />,
            color: "#1890ff",
          },
          {
            title: "Checked",
            value: checkedCount,
            icon: <CalendarOutlined />,
            color: "#faad14",
          },
          {
            title: "Verified",
            value: verifiedCount,
            icon: <CheckCircleOutlined />,
            color: "#52c41a",
          },
          {
            title: "SLA Breaches",
            value: slaBreachCount,
            icon: <DollarOutlined />,
            color: "#ff4d4f",
          },
        ],
        advancedFilter: {
          visible: drawerVisible,
          onClose: () => setDrawerVisible(false),
          onOpen: () => setDrawerVisible(true),
          content: (
            <div className="p-4">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Stage</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Stage"
                    value={filters.stage}
                    onChange={(value) =>
                      setFilters({ ...filters, stage: value })
                    }
                  >
                    <Option value="Submitted">Submitted</Option>
                    <Option value="Checked">Checked</Option>
                    <Option value="Verified">Verified</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Contractor</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contractor"
                    value={filters.contractor}
                    onChange={(value) =>
                      setFilters({ ...filters, contractor: value })
                    }
                  >
                    <Option value="ABC Infra Ltd">ABC Infra Ltd</Option>
                    <Option value="XYZ Constructions">XYZ Constructions</Option>
                    <Option value="BuildTech Pvt Ltd">BuildTech Pvt Ltd</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">RA Bill #</label>
                  <Input
                    placeholder="Enter RA Bill Number"
                    value={filters.raBillNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, raBillNumber: e.target.value })
                    }
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Work Order #</label>
                  <Input
                    placeholder="Enter WO Number"
                    value={filters.woNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, woNumber: e.target.value })
                    }
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Site</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Site"
                    value={filters.site}
                    onChange={(value) =>
                      setFilters({ ...filters, site: value })
                    }
                  >
                    <Option value="Urban Rise - Tower A">
                      Urban Rise - Tower A
                    </Option>
                    <Option value="Urban Rise - Tower B">
                      Urban Rise - Tower B
                    </Option>
                    <Option value="Urban Rise - Tower C">
                      Urban Rise - Tower C
                    </Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Submission Date Range</label>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={filters.dateRange}
                    onChange={(dates) =>
                      setFilters({ ...filters, dateRange: dates })
                    }
                    placeholder={["Submit From", "Submit To"]}
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">SLA Status</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select SLA Status"
                    value={filters.slaStatus}
                    onChange={(value) =>
                      setFilters({ ...filters, slaStatus: value })
                    }
                  >
                    <Option value="within">Within SLA</Option>
                    <Option value="breach">SLA Breach</Option>
                  </Select>
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button
                  type="primary"
                  onClick={applyFilters}
                  style={{ flex: 1 }}
                >
                  Apply Filters
                </Button>
                <Button onClick={resetFilters} style={{ flex: 1 }}>
                  Reset
                </Button>
              </div>
            </div>
          ),
        },
      }}
    />
  );
};

export default RABillsPendingApprovals;
